import {
  useAccountClient,
  darkThemeStyles,
  JupiterTheme,
  useAsyncEffect,
  useSnackbarNotification,
  useFeatureFlags,
  useJ1AuthFetch,
  useAccounts,
} from '@jupiterone/jupiter-web-toolkit';
import { Button, TextField, Typography } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import clsx from 'clsx';
import { subDays } from 'date-fns';
import React, { useState } from 'react';
import { useArrayState } from 'react-use-object-state';
import { UsageDataPoint } from 'navbar/src/jupiterAccountSettings/types';
import AccountLogo from 'navbar/src/jupiterAccountSettings/AccountSettings/components/AccountLogo';
import AccountManagementChart from './AccountManagementChart';
import { getUsage } from './usageClient';
import CompanyLogoSetting from './CompanyLogoSetting';

const useStyles = makeStyles((theme: JupiterTheme) =>
  createStyles({
    ...darkThemeStyles,
    container: {
      alignItems: 'center',
      display: 'flex',
      flexFlow: 'column',
      marginTop: 25,
      width: '100%',
    },

    white: { color: theme.palette.common.white },
    form: {
      marginTop: theme.spacing(5),
      marginBottom: theme.spacing(5),
    },
    buttons: {
      margin: theme.spacing(2, 0, 4, 0),
      display: 'flex',
      'justify-content': 'flex-end',
      '& button:last-child': {
        'margin-left': theme.spacing(2),
      },
    },
    sectionHeader: {
      margin: theme.spacing(2, 0),
    },
  })
);

const getDaysAgoTimestamp = (daysAgo: number): number =>
  subDays(new Date(), 30).getTime();

interface FormState {
  accountName?: string;
}

const AccountManagementPage = () => {
  const classes = useStyles();
  const { account } = useAccounts();
  const { authFetch } = useJ1AuthFetch();
  const accountClient = useAccountClient();
  const notification = useSnackbarNotification();
  const [formState, setFormState] = useState<FormState>({});
  const usageDataPointState = useArrayState<UsageDataPoint>([]);
  const { hasFeature } = useFeatureFlags();
  const canViewLogoFeature = hasFeature('accountLogoSettings');
  useAsyncEffect(async () => {
    const response = await getUsage({
      authFetch,
      usageOptions: {
        timeRangeType: 'day',
        startTimestamp: getDaysAgoTimestamp(30),
        endTimestamp: new Date().getTime(),
      },
    });
    usageDataPointState.setState(response.data);
  }, []);

  const InputLabelProps = {
    className: classes.textInputLabel,
    classes: {
      disabled: classes.textInputLabelDisabled,
      focused: classes.textInputLabelFocused,
    },
  };
  const InputProps = {
    className: classes.textInput,
    classes: {
      disabled: classes.disabled,
      icon: classes.white,
    },
  };

  const onFormStateChange = (propertyName: keyof FormState) => ({
    target: { value },
  }: React.ChangeEvent<HTMLInputElement>) => {
    setFormState((prevState) => ({ ...prevState, [propertyName]: value }));
  };

  const onResetFormState = () => {
    setFormState({});
  };

  const onSubmitForm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formState.accountName) {
      try {
        await accountClient.patchAccount({
          name: formState.accountName,
        });
        notification.success('Successfully updated account name', 3000);
      } catch (e) {
        setFormState({});
        notification.error({
          message: 'Failed to update account name',
          error: e,
          autoHideDuration: 3000,
        });
      }
    }
  };

  return (
    <div className={classes.container}>
      <AccountLogo size={'5rem'} />
      <form onSubmit={onSubmitForm} className={classes.form}>
        <TextField
          id="accountId"
          label="Account Id"
          value={account?.id || 'none'}
          className={(classes as any).textField}
          InputLabelProps={InputLabelProps}
          InputProps={InputProps}
          margin="normal"
          fullWidth
          disabled
        />
        <TextField
          id="accountName"
          label="Account Name"
          required
          value={
            formState.accountName !== undefined
              ? formState.accountName
              : account?.name
          }
          className={(classes as any).textField}
          onChange={onFormStateChange('accountName')}
          InputLabelProps={InputLabelProps}
          InputProps={InputProps}
          margin="normal"
          fullWidth
        />
        <TextField
          id="owner"
          label="Owner"
          value={account?.owner || 'none'}
          className={(classes as any).textField}
          InputLabelProps={InputLabelProps}
          InputProps={InputProps}
          margin="normal"
          fullWidth
          disabled
        />
        <div className={classes.buttons}>
          <Button
            id="reset"
            disabled={Object.values(formState).length === 0}
            variant="contained"
            color="default"
            onClick={onResetFormState}
          >
            Reset
          </Button>
          <Button
            id="submit"
            type="submit"
            disabled={
              !formState.accountName || Object.values(formState).length === 0
            }
            variant="contained"
            color="primary"
          >
            Save
          </Button>
        </div>
        {canViewLogoFeature && <CompanyLogoSetting />}
      </form>

      <Typography
        variant="h6"
        component="h6"
        className={clsx(classes.sectionHeader, classes.white)}
      >
        Account Usage
      </Typography>

      <AccountManagementChart usageDataPoints={usageDataPointState.state} />
    </div>
  );
};
export default AccountManagementPage;
